import { useCallback, useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';

import {
  recordFeedEvents,
  type FeedEventUpload,
  type FeedEventType,
  type FeedKind,
} from './post-service';

type QueuedFeedEvent = {
  dwellBucket?: number;
  postId: string;
  type: FeedEventType;
};

const FEED_EVENT_REPORT_INTERVAL_MS = 60_000;
const FEED_EVENT_UPLOAD_LIMIT = 200;
const LOW_VALUE_EVENT_TYPES = new Set<FeedEventType>(['dwell', 'impression']);

function hasFeedEventStats(summary: FeedEventUpload) {
  return (
    Boolean(summary.hidden) ||
    Boolean(summary.lessLikeThis) ||
    (summary.commentOpenCount ?? 0) > 0 ||
    (summary.detailOpenCount ?? 0) > 0 ||
    (summary.postEngagementCount ?? 0) > 0
  );
}

function mergeFeedEvent(
  summary: FeedEventUpload,
  event: QueuedFeedEvent,
): FeedEventUpload {
  if (event.type === 'detail_open') {
    summary.detailOpenCount = (summary.detailOpenCount ?? 0) + 1;
  } else if (event.type === 'comment_open') {
    summary.commentOpenCount = (summary.commentOpenCount ?? 0) + 1;
  } else if (event.type === 'post_engagement') {
    summary.postEngagementCount = (summary.postEngagementCount ?? 0) + 1;
  } else if (event.type === 'hide') {
    summary.hidden = true;
  } else if (event.type === 'less_like_this') {
    summary.lessLikeThis = true;
  }

  return summary;
}

export function useFeedEvents(feedKind: FeedKind, uid?: string | null) {
  const pendingRef = useRef(new Map<string, FeedEventUpload>());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const impressionPostIdsRef = useRef(new Set<string>());
  const uidRef = useRef(uid);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!uidRef.current) {
      pendingRef.current.clear();
      return;
    }

    const events = Array.from(pendingRef.current.values())
      .filter(hasFeedEventStats)
      .slice(0, FEED_EVENT_UPLOAD_LIMIT);

    if (events.length === 0) {
      return;
    }

    events.forEach((event) => pendingRef.current.delete(event.postId));

    void recordFeedEvents(events).catch(() => {
      events.forEach((event) => {
        const existing = pendingRef.current.get(event.postId);
        pendingRef.current.set(event.postId, {
          ...event,
          commentOpenCount:
            (existing?.commentOpenCount ?? 0) + (event.commentOpenCount ?? 0),
          detailOpenCount:
            (existing?.detailOpenCount ?? 0) + (event.detailOpenCount ?? 0),
          hidden: Boolean(existing?.hidden) || Boolean(event.hidden),
          lessLikeThis:
            Boolean(existing?.lessLikeThis) || Boolean(event.lessLikeThis),
          postEngagementCount:
            (existing?.postEngagementCount ?? 0) +
            (event.postEngagementCount ?? 0),
        });
      });

      if (!timerRef.current) {
        timerRef.current = setTimeout(flush, FEED_EVENT_REPORT_INTERVAL_MS);
      }
    });
  }, []);

  const recordEvent = useCallback(
    (event: QueuedFeedEvent, immediate = false) => {
      if (!uidRef.current || LOW_VALUE_EVENT_TYPES.has(event.type)) {
        return;
      }

      const current = pendingRef.current.get(event.postId) ?? {
        feedKind,
        postId: event.postId,
      };
      pendingRef.current.set(event.postId, mergeFeedEvent(current, event));

      if (
        immediate &&
        (event.type === 'hide' || event.type === 'less_like_this')
      ) {
        flush();
        return;
      }

      if (!timerRef.current) {
        timerRef.current = setTimeout(flush, FEED_EVENT_REPORT_INTERVAL_MS);
      }
    },
    [feedKind, flush],
  );

  const recordImpression = useCallback((postId: string) => {
    if (impressionPostIdsRef.current.has(postId)) {
      return;
    }

    impressionPostIdsRef.current.add(postId);
  }, []);

  useEffect(() => {
    if (uidRef.current && uidRef.current !== uid) {
      flush();
    }

    uidRef.current = uid;
    if (!uid) {
      pendingRef.current.clear();
      impressionPostIdsRef.current.clear();
    }
  }, [flush, uid]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'background' || state === 'inactive') {
        flush();
      }
    });

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('beforeunload', flush);
    }

    return () => {
      subscription.remove();
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', flush);
      }
      flush();
    };
  }, [flush]);

  return { flush, recordEvent, recordImpression };
}
